/**
 * One scan's events -> the sounds to play (05 §2). At most one of each kind per scan: ten
 * boulders landing together make one somewhat louder thud, panned to where they fell on
 * average. Pure.
 */

import type { SimEvent } from '../sim/types';

export type SoundId =
  | 'dig' | 'collect' | 'key' | 'clock' | 'landBoulder' | 'landDiamond' | 'push'
  | 'explode' | 'explodeButterfly' | 'magicConvert' | 'born' | 'died' | 'gateOpen' | 'won'
  | 'tick' | 'timeout' | 'doorOpen' | 'expand' | 'slime';

/** Voice-steal ranks (voicePool.ts). Ambience sits below all of these, cues and UI at `CUE`. */
export const AMBIENT = 0;
export const CUE = 3;

export const PRIORITY: Readonly<Record<SoundId, number>> = {
  dig: 1, push: 1, expand: 1, slime: 1, magicConvert: 1, landDiamond: 1,
  landBoulder: 2, collect: 2, key: 2, clock: 2, doorOpen: 2, tick: 2,
  explode: 3, explodeButterfly: 3, died: 3, born: 3, gateOpen: 3, won: 3, timeout: 3,
};

/** Sounds loud enough to pull the in-cave music down under them. */
export const DUCKS: ReadonlySet<SoundId> = new Set<SoundId>([
  'explode', 'explodeButterfly', 'died', 'gateOpen', 'won', 'timeout', 'landBoulder',
]);

/** Cells either side of the listener at which the pan reaches its limit. */
const PAN_CELLS = 16;
/** Gentle: never hard left or right. */
export const MAX_PAN = 0.6;

export interface Hit {
  readonly sound: SoundId;
  /** How many events of this kind fell in the scan. */
  readonly count: number;
  /** -MAX_PAN (left) .. MAX_PAN (right). */
  readonly pan: number;
  /** The sound's parameter: seconds left for `tick`, else 0. */
  readonly n: number;
}

export function soundOf(e: SimEvent): SoundId {
  switch (e.type) {
    case 'collect': return e.what === 'diamond' ? 'collect' : e.what;
    case 'land': return e.what === 'boulder' ? 'landBoulder' : 'landDiamond';
    case 'explode': return e.kind === 'diamonds' ? 'explodeButterfly' : 'explode';
    default: return e.type;
  }
}

export function panFor(x: number, listenerX: number): number {
  const p = (x - listenerX) / PAN_CELLS;
  return Math.max(-1, Math.min(1, p)) * MAX_PAN;
}

/** Gain for `count` of a kind at once: 1 for one, a little more for many, never double. */
export function loudness(count: number): number {
  if (count <= 1) return 1;
  return Math.min(1.6, 1 + 0.3 * Math.log2(count));
}

interface Group {
  count: number;
  sumX: number;
  nX: number;
  n: number;
  order: number;
}

/** The scan's sounds, most important first (so a full voice pool drops the trivial ones). */
export function collapse(events: readonly SimEvent[], listenerX: number): Hit[] {
  const groups = new Map<SoundId, Group>();
  const add = (id: SoundId, x: number | null, n: number): void => {
    let g = groups.get(id);
    if (!g) {
      g = { count: 0, sumX: 0, nX: 0, n, order: groups.size };
      groups.set(id, g);
    }
    g.count++;
    g.n = Math.min(g.n, n);
    if (x !== null) {
      g.sumX += x;
      g.nX++;
    }
  };
  for (const e of events) {
    add(soundOf(e), 'x' in e ? e.x : null, e.type === 'tick' ? e.seconds : 0);
  }
  // Death sounds like the explosion plus a falling tone; the blast usually arrives as its own
  // event, but if Digby died some other way the explosion still has to be heard.
  const died = groups.get('died');
  if (died && !groups.has('explode') && !groups.has('explodeButterfly')) {
    groups.set('explode', { ...died, count: 1, order: died.order });
  }
  const hits: (Hit & { order: number })[] = [];
  for (const [sound, g] of groups) {
    const pan = g.nX > 0 ? panFor(g.sumX / g.nX, listenerX) : 0;
    hits.push({ sound, count: g.count, pan, n: g.n, order: g.order });
  }
  hits.sort((a, b) => PRIORITY[b.sound] - PRIORITY[a.sound] || a.order - b.order);
  return hits.map(({ sound, count, pan, n }) => ({ sound, count, pan, n }));
}
